import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';

/**
 * Thin wrapper around `@angular/cdk/dialog`'s `Dialog` service. Unlike the key-based
 * `DialogService`, this spawns the dialog's overlay purely from code - no host tag needed
 * in a template - and `disableClose` covers both backdrop-click and Escape-key dismissal.
 */
@Injectable({
  providedIn: 'root',
})
export class CdkDialogService {
  private readonly dialog = inject(Dialog);

  public open<T, R = unknown, D = unknown>(
    component: ComponentType<T>,
    config?: DialogConfig<D, DialogRef<R, T>>
  ): DialogRef<R, T> {
    const dialogRef = this.dialog.open<R, D, T>(component, {
      hasBackdrop: true,
      ...config,
    });
    // CDK creates the dialog's content view and defers applying the position strategy until
    // Angular's afterNextRender hook fires. Under zoneless CD that hook - and the content's
    // first render - aren't reliably scheduled on their own.
    dialogRef.componentRef?.changeDetectorRef.detectChanges();
    dialogRef.overlayRef.updatePosition();
    return dialogRef;
  }

  public closeAll(): void {
    this.dialog.closeAll();
  }
}
